import { useMemo } from "preact/hooks";
import { marked } from "marked";
import DOMPurify from "dompurify";

type Props = {
  source: string;
  variant?: "document" | "chat";
  streaming?: boolean;
};

const FM_RE = /^---\r?\n[\s\S]*?\r?\n---\r?\n?/;
const STREAMING_CLASSES = [
  "[&>:last-child]:after:ml-1 [&>:last-child]:after:inline-block",
  "[&>:last-child]:after:h-2 [&>:last-child]:after:w-1.5",
  "[&>:last-child]:after:animate-pulse [&>:last-child]:after:bg-fg-faint",
  "[&>:last-child]:after:align-text-bottom [&>:last-child]:after:content-['']",
].join(" ");

// Beyond script-free HTML, block what lets a member's content dress up as UI:
// forms/controls could post to owner-only endpoints on this origin, and
// inline styles could overlay the real app with a fake one.
const SANITIZE_CONFIG = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ["form", "input", "button", "select", "textarea", "style"],
  FORBID_ATTR: ["style"],
};

// Links in rendered content (except in-page #anchors) open in a new tab. Same-origin hrefs would
// otherwise be intercepted by preact-iso's global click router and navigate
// the SPA away from an unsaved draft without any confirmation.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  const href = node.getAttribute("href");
  if (node.tagName === "A" && href && !href.startsWith("#")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

// marked >=8 dropped its built-in sanitizer, so raw HTML in authored content
// and AI responses flows through unchanged. A platform admin shares this origin
// with the API, so injected markup must be sanitized before it reaches the DOM.
export function MarkdownPreview({
  source,
  variant = "document",
  streaming = false,
}: Props) {
  const html = useMemo(() => {
    if (streaming) return null;
    const markdown = variant === "document" ? source.replace(FM_RE, "") : source;
    const rendered = marked.parse(markdown, { async: false }) as string;
    return DOMPurify.sanitize(rendered, SANITIZE_CONFIG);
  }, [source, streaming, variant]);

  const documentClasses =
    "rounded-lg border border-border bg-bg p-6 shadow-xs";
  const chatClasses = [
    "prose-sm mr-6 rounded-lg border border-border px-3 py-2 text-fg-muted",
    "[&>:first-child]:mt-0 [&>:last-child]:mb-0",
    "prose-headings:my-2 prose-headings:text-base",
    "prose-p:my-2 prose-p:text-fg-muted",
    "prose-ol:my-2 prose-ul:my-2 prose-li:my-0 prose-li:text-fg-muted",
    "prose-pre:my-2 prose-pre:overflow-x-auto",
  ].join(" ");

  const classes = [
    "prose prose-zinc max-w-none",
    variant === "chat" ? chatClasses : documentClasses,
    streaming ? `${STREAMING_CLASSES} whitespace-pre-wrap` : "",
    "prose-headings:tracking-tight prose-headings:text-fg",
    "prose-a:text-fg prose-a:underline-offset-2 hover:prose-a:text-fg-muted",
    "prose-strong:text-fg",
    "prose-code:rounded prose-code:bg-bg-inset prose-code:px-1 prose-code:py-0.5 prose-code:text-[0.85em] prose-code:font-medium prose-code:text-fg",
    "prose-code:before:content-none prose-code:after:content-none",
    "prose-pre:bg-zinc-900",
  ].join(" ");

  if (streaming) {
    return (
      <div class={classes} aria-busy="true">
        <span>{source}</span>
      </div>
    );
  }

  return (
    <div
      class={classes}
      dangerouslySetInnerHTML={{ __html: html ?? "" }}
    />
  );
}
